import { getSupabase } from './supabase';

/**
 * Typed client for the Partner School Portfolio edge functions and the
 * schools_sa read policy. The browser never holds service-role credentials;
 * all privileged operations go through the deployed edge functions.
 */

export interface InvitationContext {
  invitation: {
    id: string;
    email: string;
    status: string;
    expires_at: string | null;
  };
  partner: { id: string; name: string; slug: string } | null;
  representative: {
    id: string;
    full_name: string;
    email: string;
    role: string;
  } | null;
}

export interface AccessSession {
  id: string;
  expires_at: string;
}

export interface PortfolioSchool {
  id: string;
  nat_emis: number;
  status: 'added' | 'removed' | 'unchanged';
  source: 'manual' | 'spreadsheet' | 'mixed';
  created_by: string | null;
  import_job_id: string | null;
  created_at: string;
  school: {
    nat_emis: number;
    official_institution_name: string;
    province: string | null;
    district: string | null;
    sector: string | null;
    phase_ped: string | null;
    quintile: string | null;
  } | null;
}

/**
 * A school formally selected/assigned to the current partner pilot
 * (Group A). Sourced from the scoped pilot registry
 * (partner_pilot_schools), joined to the authoritative schools_sa master
 * for display details. The original spreadsheet values are preserved, and
 * authoritative details are only populated where the school matched
 * schools_sa (ADR-001 Decision 7 — pilot governance).
 */
export interface PilotSchool {
  id: string;
  programme_id: string;
  partner_id: string;
  nat_emis: number | null;
  /** Original spreadsheet school name (preserved). */
  original_name: string;
  /** Original spreadsheet district (preserved). */
  original_district: string | null;
  /** Province from the source workbook (preserved). */
  source_province: string | null;
  /** Source workbook filename. */
  source_workbook: string;
  /** 1-based row number in the source workbook. */
  source_row_number: number | null;
  selection_status: string;
  approval_source: string | null;
  effective_date: string | null;
  match_status: 'matched' | 'needs_review' | 'ambiguous' | 'unmatched';
  match_method: string | null;
  match_reason: string | null;
  match_score: number | null;
  candidate_emis: number[] | null;
  /** Authoritative details joined from schools_sa (null when unmatched). */
  school: {
    nat_emis: number;
    official_institution_name: string;
    province: string | null;
    district: string | null;
    sector: string | null;
    phase_ped: string | null;
    quintile: string | null;
  } | null;
}

/**
 * A school with active platform users (Group B). Active-user counts follow
 * ADR-001 Decision 5: only valid learner grade assignments count. Sourced
 * from production-derived public.profiles matched to the authoritative
 * public.schools_sa master (public.institutions is legacy and excluded).
 */
export interface ActiveUserSchool {
  school_name: string;
  nat_emis: number | null;
  province: string | null;
  district: string | null;
  active_user_count: number;
}

export interface Portfolio {
  id: string;
  version: number;
  source: 'manual' | 'spreadsheet' | 'mixed';
  school_count: number;
  created_at: string;
}

/**
 * M4-R21B: full authoritative identity for a candidate school, surfaced at the
 * match-review step so same-name schools can be disambiguated by location.
 */
export interface ImportCandidateSchool {
  nat_emis: number;
  official_institution_name: string;
  province: string | null;
  district: string | null;
  street_address: string | null;
}

export interface ImportResult {
  match_status:
    | 'matched_auto'
    | 'needs_confirmation'
    | 'multiple_matches'
    | 'not_found'
    | 'excluded';
  matched_nat_emis: number | null;
  match_method: string | null;
  confidence: number | null;
  candidate_ids: number[] | null;
  // M4-R21B: full identity for each candidate nat_emis (name/EMIS/province/
  // district/address) so the match-review step can show candidates individually.
  candidates?: ImportCandidateSchool[] | null;
}

/**
 * M4-R21B: Relationship indicators must follow the school. The import edge
 * function enriches matched and candidate nat_emis with Reslocate relationship
 * data (App Users / Pilot / Both) so the match-review step can surface the
 * indicators alongside each candidate. Existing activity is contextual info,
 * never a rejection.
 */
export interface ImportRelationships {
  relationships: SchoolRelationshipsMap;
}

export interface ImportJobSummary {
  id: string;
  matched_rows: number;
  needs_confirmation_rows: number;
  multiple_match_rows: number;
  not_found_rows: number;
  excluded_rows: number;
}

export interface ImportRowInput {
  emis: string | null;
  name: string | null;
  province: string | null;
  district: string | null;
}

export interface ColumnMapping {
  emis: number | null;
  name: number | null;
  province: number | null;
  district: number | null;
}

interface ApiErrorBody {
  success?: boolean;
  error?: string;
}

/**
 * Map a backend error message to a safe, human-readable message.
 * Never surfaces internal stack traces, credentials, or raw server text.
 */
function toHumanError(raw: string | undefined, fallback: string): string {
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (lower.includes('invalid verification code')) return 'Incorrect verification code. Please check and try again.';
  if (lower.includes('verification code expired')) return 'The verification code has expired. Request a new one.';
  if (lower.includes('no verification code requested')) return 'No verification code has been requested yet. Send a code first.';
  if (lower.includes('too many failed attempts')) return 'Too many failed attempts. Please try again in 15 minutes.';
  if (lower.includes('please wait')) return raw; // e.g. "Please wait 42s before requesting another code"
  if (lower.includes('invitation expired')) return 'This invitation has expired. Please request a new invitation.';
  if (lower.includes('invitation revoked')) return 'This invitation has been revoked. Please contact Reslocate.';
  if (lower.includes('invitation not found')) return 'This invitation could not be found. Please check your token.';
  if (lower.includes('email does not match')) return 'This email does not match the invitation.';
  if (lower.includes('unable to send verification code')) return 'Unable to send the verification code. Please try again.';
  if (lower.includes('internal server error')) return 'Verification service is temporarily unavailable. Please try again.';
  if (lower.includes('missing verification code')) return 'Enter the 6-digit verification code.';
  if (lower.includes('invalid invitation token') || lower.includes('invalid token')) {
    return 'Invalid invitation token. Please check and try again.';
  }
  return fallback;
}

async function postEdgeFunction<T>(
  functionName: string,
  body: Record<string, unknown>,
): Promise<T> {
  const supabase = getSupabase();
  const { data, error } = await supabase.functions.invoke<T & ApiErrorBody>(
    functionName,
    { body },
  );

  if (error) {
    // supabase-js surfaces a generic "Edge Function returned a non-2xx status
    // code" for HTTP errors. The real, safe message lives in the response body
    // (FunctionsHttpError.context). Parse it so the user sees a meaningful
    // message instead of the generic one (M4-R15 Task 5).
    const ctx = (error as { context?: Response }).context;
    let serverMessage: string | undefined;
    if (ctx) {
      try {
        const parsed = (await ctx.json()) as ApiErrorBody;
        serverMessage = parsed?.error;
      } catch {
        serverMessage = undefined;
      }
    }
    throw new Error(
      toHumanError(serverMessage, error.message || `Edge function ${functionName} failed`),
    );
  }
  if (data && data.success === false) {
    throw new Error(toHumanError(data.error, `Edge function ${functionName} failed`));
  }
  if (!data) {
    throw new Error(`Edge function ${functionName} returned no data`);
  }
  return data;
}

// ── M4-R20A: Supabase Auth activation & membership ──────────────────────────

export interface ActivateVerifyResult {
  success: boolean;
  email: string;
  inviteTokenHash: string;
}

export interface ActivateCompleteResult {
  success: boolean;
  message: string;
  partner: { id: string; name: string; slug: string } | null;
}

/**
 * Verify the app-side activation token and obtain a one-time Supabase Auth
 * invite token hash so the invitee can establish a session and set a password.
 */
export function activateVerify(token: string): Promise<ActivateVerifyResult> {
  return postEdgeFunction<ActivateVerifyResult>('partner-auth-activate', {
    action: 'verify',
    token,
  });
}

/**
 * Mark the invitation accepted after the invitee has set their password.
 * Requires an authenticated Supabase Auth session (JWT attached automatically).
 */
export function activateComplete(): Promise<ActivateCompleteResult> {
  return postEdgeFunction<ActivateCompleteResult>('partner-auth-activate', {
    action: 'complete',
  });
}

export interface ResendActivationResult {
  success: boolean;
  message: string;
}

/**
 * M4-R21B — Partner-facing "resend my activation email" flow.
 *
 * Accepts an email address and asks the server to resend the activation email
 * if (and only if) an eligible pending invitation exists. The endpoint is
 * enumeration-resistant: it always returns the same generic message whether or
 * not an invitation exists, so this call never reveals whether an email has an
 * invitation. No authentication is required (the invitee has not activated yet).
 */
export function resendActivation(email: string): Promise<ResendActivationResult> {
  return postEdgeFunction<ResendActivationResult>('partner-resend-activation', {
    email,
  });
}

/** Read the current portfolio (latest valid version) for the session. */
export function readPortfolio(): Promise<{
  success: boolean;
  partnerName: string;
  portfolio: Portfolio | null;
  schools: PortfolioSchool[];
}> {
  return postEdgeFunction('partner-portfolio', {
    action: 'read',
  });
}

/**
 * M4-R18E: A business-active school as exposed by the authoritative Active
 * Schools contract. This is the PUBLIC shape only — it deliberately omits
 * candidate EMIS, fuzzy scores, match_method, identity_method, evidence_basis,
 * and any forensic/candidate-generation internals.
 */
export type ReslocateRelationship = 'app_users' | 'pilot' | 'both';

export interface BusinessActiveSchool {
  business_active_school_id: string;
  nat_emis: number | null;
  official_school_name: string | null;
  province: string | null;
  district: string | null;
  total_learners: number;
  grades_represented: string[];
  learners_by_grade: Record<string, number>;
  identity_status: 'CONFIRMED_NAT_EMIS' | 'CANONICAL_IDENTITY_PENDING' | null;
  // M4-R21B: Reslocate relationship attributes (independent, coexisting).
  is_pilot: boolean;
  relationship: ReslocateRelationship;
}

export type BusinessActiveSchoolSortField =
  // M4-R21F: default sort — App User priority (relationship → learner_count
  // DESC → name ASC → EMIS ASC), computed at query level by the UNION RPC.
  | 'app_user_priority'
  | 'official_school_name'
  | 'canonical_nat_emis'
  | 'province'
  | 'district'
  | 'learner_count';

export interface BusinessActiveSchoolsParams {
  query?: string;
  page?: number;
  pageSize?: number;
  sortField?: BusinessActiveSchoolSortField;
  sortDir?: 'asc' | 'desc';
  province?: string | null;
  district?: string | null;
  grade?: string | null;
  identityStatus?: string | null;
  relationship?: ReslocateRelationship | 'all' | null;
}

export interface BusinessActiveSchoolsResponse {
  rows: BusinessActiveSchool[];
  totalCount: number;
  // M4-R21F: data-derived grade filter options from the filtered population
  // (no hard-coded R-12 list).
  grades: string[];
}

/**
 * M4-R18E: Server-side paginated read of the AUTHORITATIVE business-active
 * school population.
 *
 * The dataset is the FROZEN 1,489-school register. This endpoint pages the
 * result server-side and applies search/filter/sort across the COMPLETE
 * dataset, so the browser never loads the full list into the DOM.
 */
export function readBusinessActiveSchools(
  params: BusinessActiveSchoolsParams,
): Promise<BusinessActiveSchoolsResponse> {
  return postEdgeFunction('partner-portfolio', {
    action: 'readBusinessActiveSchools',
    query: params.query ?? '',
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 25,
    // M4-R21F: default sort is App User priority.
    sortField: params.sortField ?? 'app_user_priority',
    sortDir: params.sortDir ?? 'asc',
    province: params.province ?? null,
    district: params.district ?? null,
    grade: params.grade ?? null,
    identityStatus: params.identityStatus ?? null,
    relationship: params.relationship ?? 'all',
  });
}

/**
 * M4-R21B: Relationship indicators must follow the school. Manual search runs
 * browser-side via PostgREST (RLS read policy on schools_sa), so it cannot
 * access the service-role-only business-active register or the partner's pilot
 * set. This endpoint enriches a set of nat_emis values with the Reslocate
 * relationship data (App Users / Pilot / Both) for display at manual search,
 * selection, match review, and saved portfolio review.
 */
export interface SchoolRelationship {
  is_pilot: boolean;
  relationship: ReslocateRelationship | null;
  learner_count: number;
  in_register: boolean;
}

export type SchoolRelationshipsMap = Record<string, SchoolRelationship>;

export function readSchoolRelationships(
  natEmis: number[],
): Promise<{ relationships: SchoolRelationshipsMap }> {
  return postEdgeFunction('partner-portfolio', {
    action: 'readSchoolRelationships',
    natEmis,
  });
}

export interface SaveSchoolInput {
  nat_emis: number;
  status: 'added' | 'removed' | 'unchanged';
  source: 'manual' | 'spreadsheet' | 'mixed';
  import_job_id?: string | null;
}

/** Save a new immutable portfolio version. */
export function savePortfolio(
  source: 'manual' | 'spreadsheet' | 'mixed',
  schools: SaveSchoolInput[],
): Promise<{ success: boolean; portfolio: Portfolio }> {
  return postEdgeFunction('partner-portfolio', {
    action: 'save',
    source,
    schools,
  });
}

/**
 * M4-R21K (PO-A): Remove one or more schools from the partner's CURRENT saved
 * portfolio. The edge function delegates to the transactional
 * remove_partner_portfolio_schools RPC, which writes a new immutable
 * complete-snapshot version without those schools inside a single transaction.
 */
export function removePortfolioSchools(
  natEmis: number[],
): Promise<{ success: boolean; portfolio: Portfolio }> {
  return postEdgeFunction('partner-portfolio', {
    action: 'remove',
    natEmis,
  });
}

/** Run the spreadsheet matching pipeline and create an import job. */
export function runImport(
  filename: string,
  fileType: 'xlsx' | 'csv',
  columnMapping: ColumnMapping,
  rows: ImportRowInput[],
): Promise<{
  success: boolean;
  importJob: ImportJobSummary;
  results: ImportResult[];
  relationships?: SchoolRelationshipsMap;
}> {
  return postEdgeFunction('partner-import', {
    filename,
    fileType,
    columnMapping,
    rows,
  });
}

export interface SchoolSearchResult {
  nat_emis: number;
  official_institution_name: string;
  province: string | null;
  district: string | null;
  sector: string | null;
  phase_ped: string | null;
  quintile: string | null;
}

export type SchoolSearchSortField =
  | 'official_institution_name'
  | 'province'
  | 'district'
  | 'nat_emis';

export interface SchoolSearchParams {
  query: string;
  page?: number;
  pageSize?: number;
  sortField?: SchoolSearchSortField;
  sortDir?: 'asc' | 'desc';
}

export interface SchoolSearchResponse {
  rows: SchoolSearchResult[];
  totalCount: number;
}

/**
 * Search the authoritative schools_sa master via the approved read policy.
 * The browser may read schools_sa (RLS read policy), but never write it.
 *
 * Server-side pagination: the browser never retrieves the full 25,528-row
 * master. PostgREST `.range()` pages the result set and `count: 'exact'`
 * returns the total matching count so the UI can render totals and page
 * counts without loading every row.
 */
/**
 * Look up authoritative school identity (official name, province, district)
 * for a set of nat_emis values. Used at identity decision points such as
 * Review & Save, where the draft only carries nat_emis/status/source and the
 * full school identity must be surfaced alongside the relationship indicators.
 *
 * The browser may read schools_sa via the approved RLS read policy, but never
 * write it. Returns a map keyed by nat_emis so callers can enrich rows without
 * re-querying the master.
 */
export async function readSchoolsByIds(
  natEmis: number[],
): Promise<Record<number, SchoolSearchResult>> {
  const supabase = getSupabase();
  const ids = [...new Set(natEmis)].filter((e) => e > 0);
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('schools_sa')
    .select(
      'nat_emis, official_institution_name, province, district, sector, phase_ped, quintile',
    )
    .in('nat_emis', ids);
  if (error) {
    throw new Error(error.message || 'School lookup failed');
  }
  const map: Record<number, SchoolSearchResult> = {};
  for (const row of data ?? []) {
    map[row.nat_emis] = row as SchoolSearchResult;
  }
  return map;
}

export async function searchSchools(
  params: SchoolSearchParams,
): Promise<SchoolSearchResponse> {
  const supabase = getSupabase();
  const trimmed = params.query.trim();
  if (!trimmed) {
    return { rows: [], totalCount: 0 };
  }

  const page = Math.max(1, params.page ?? 1);
  const pageSize = Math.max(1, params.pageSize ?? 25);
  const sortField = params.sortField ?? 'official_institution_name';
  const sortDir = params.sortDir ?? 'asc';
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await supabase
    .from('schools_sa')
    .select(
      'nat_emis, official_institution_name, province, district, sector, phase_ped, quintile',
      { count: 'exact' },
    )
    .or(
      `official_institution_name.ilike.%${trimmed}%,` +
        `nat_emis.eq.${trimmed.replace(/\D/g, '') || '0'}`,
    )
    .order(sortField, { ascending: sortDir === 'asc' })
    .range(from, to);

  if (error) {
    throw new Error(error.message || 'School search failed');
  }
  return {
    rows: (data ?? []) as SchoolSearchResult[],
    totalCount: count ?? 0,
  };
}
